import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./pages/Home/Home";
import Learning from "./pages/Learning/Learning";
import Courses from "./pages/Courses";
import LiveClasses from "./pages/LiveClasses";
import CourseVideoDetails from "./pages/CourseVideoDetails";
import CourseDetails from "./pages/CourseDetails";
import LiveSessionDetails from "./pages/LiveSessionDetails";
import LiveClassroom from "./pages/LiveClassroom";
import { setPageTitle } from "./utils/seo";
import { AuthProvider } from "./context/AuthContext";
import AdminLogin from "./pages/Admin/AdminLogin";
import Overview from "./pages/Admin/Overview/Overview";
import AdminLayout from "./layouts/AdminLayout";
import AdminCourses from "./pages/Admin/Courses/AdminCourses";
import AdminUsers from "./pages/Admin/Users/AdminUsers";
import AdminCategories from "./pages/Admin/Categories/AdminCategories";
import AdminSettings from "./pages/Admin/Settings/AdminSettings";
import AdminLiveSessions from "./pages/Admin/LiveSessions/AdminLiveSessions";
import AdminCalendar from "./pages/Admin/Calendar/AdminCalendar";
import AdminSubscribers from "./pages/Admin/Subscribers/AdminSubscribers";
import AdminSubscriptions from "./pages/Admin/Subscriptions/AdminSubscriptions";
import AdminCourseTools from "./pages/Admin/CourseTools/AdminCourseTools";
import AdminInstructors from "./pages/Admin/Instructors/AdminInstructors";
import { Provider } from "react-redux";
import { store } from "./store";
import { AlertProvider } from "./context/AlertContext";
import ErrorBoundary from "./components/ErrorBoundary";
// import ProtectedRoute from "./components/ProtectedRoute";
import AddLessons from "./pages/Admin/Courses/AddLessons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import InstructorClassroom from "./pages/Classroom/InstructorClassroom";
import LandingLayout from "./layouts/LandingLayout";
import Login from "./pages/Login/Login";
import Landing from "./pages/Landing/Landing";
import StartupSchool from "./pages/StartupSchool/StartupSchool";
import HighCode from "./pages/HighCode";
import AllProjects from "./pages/AllProjects";
import StartBuilding from "./pages/StartBuilding";
import CategoryDetail from "./pages/CategoryDetail";
import ProjectDetail from "./pages/ProjectDetail";
import GamesPage from "./pages/Games";
import GameDetail from "./pages/GameDetail";
import { ArduinoLEDGame, GameSelector, GamePlayRouter } from "./pages/GamePlay";
import RobotDanceArena3D from "./pages/GamePlay/RobotDanceArena3D";
import Apply from "./pages/Apply/Apply";
import Programs from "./pages/Admin/Programs/Programs";
import Applications from "./pages/Admin/Programs/Applications";
import EditProgram from "./pages/Admin/Programs/EditProgram";
import SuperAdminGuard from "./components/guards/SuperAdminGuard";
import { Projects, Games, StarterPacks, Categories, AgeGroups, Content } from "./pages/Admin/HighCode";
import AuthCallback from "./pages/AuthCallback";
import DeletionInstructions from "./pages/DeletionInstructions/DeletionInstructions";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import Pricing from "./pages/Pricing";
import Profile from "./pages/Profile";

const queryClient = new QueryClient();

const App = () => {
  React.useEffect(() => {
    setPageTitle("Learn Programming from Industry Experts");
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <AlertProvider>
            <AuthProvider>
              <Router>
              <Routes>
                <Route
                  path="/"
                  element={
                    <LandingLayout>
                      <Landing />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/startup-school"
                  element={
                    <LandingLayout>
                      <StartupSchool />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/high-code"
                  element={
                    <LandingLayout>
                      <HighCode />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/all-projects"
                  element={
                    <LandingLayout>
                      <AllProjects />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/start-building"
                  element={
                    <LandingLayout>
                      <StartBuilding />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/category/:category"
                  element={
                    <LandingLayout>
                      <CategoryDetail />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/project/:projectId"
                  element={
                    <LandingLayout>
                      <ProjectDetail />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/high-code/games"
                  element={
                    <LandingLayout>
                      <GamesPage />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/high-code/game/:gameId"
                  element={
                    <LandingLayout>
                      <GameDetail />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/high-code/game/:gameId"
                  element={
                    <LandingLayout>
                      <GameSelector />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/high-code/game/:gameId/play"
                  element={
                    <LandingLayout>
                      <GamePlayRouter />
                    </LandingLayout>
                  }
                />
                <Route
                  path="/high-code/3d-arena"
                  element={
                    <LandingLayout>
                      <RobotDanceArena3D />
                    </LandingLayout>
                  }
                />
                <Route element={<Layout />}>
                  <Route path="/code-school" element={<Home />} />
                  <Route path="/learning" element={<Learning />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/live-classes" element={<LiveClasses />} />
                  <Route
                    path="/course/:courseId/classroom"
                    element={<CourseVideoDetails />}
                  />
                  <Route
                    path="/course/:id/video"
                    element={<CourseVideoDetails />}
                  />
                  <Route
                    path="/course/:id"
                    element={<CourseDetails isEnrolled={false} />}
                  />
                  <Route
                    path="/live-session/:id"
                    element={<LiveSessionDetails />}
                  />
                  <Route path="/classroom/:id" element={<LiveClassroom />} />
                  <Route path="/profile" element={<Profile />} />
                </Route>

                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/deletion-instructions" element={<DeletionInstructions />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<Overview />} />
                  <Route path="/admin/courses" element={<AdminCourses />} />
                  <Route
                    path="/admin/courses/:courseId/lessons"
                    element={<AddLessons />}
                  />
                  <Route
                    path="/admin/live-sessions"
                    element={<AdminLiveSessions />}
                  />
                  <Route path="/admin/calendar" element={<AdminCalendar />} />
                  {/* Super Admin Only Routes */}
                  <Route path="/admin/users" element={<SuperAdminGuard><AdminUsers /></SuperAdminGuard>} />
                  <Route path="/admin/instructors" element={<SuperAdminGuard><AdminInstructors /></SuperAdminGuard>} />
                  <Route
                    path="/admin/subscribers"
                    element={<SuperAdminGuard><AdminSubscribers /></SuperAdminGuard>}
                  />
                  <Route
                    path="/admin/categories"
                    element={<AdminCategories />}
                  />
                  <Route
                    path="/admin/course-tools"
                    element={<AdminCourseTools />}
                  />
                  <Route path="/admin/settings" element={<SuperAdminGuard><AdminSettings /></SuperAdminGuard>} />
                  <Route
                    path="/admin/subscriptions"
                    element={<SuperAdminGuard><AdminSubscriptions /></SuperAdminGuard>}
                  />
                  <Route path="/admin/programs" element={<SuperAdminGuard><Programs /></SuperAdminGuard>} />
                  <Route
                    path="/admin/programs/edit/:id"
                    element={<SuperAdminGuard><EditProgram /></SuperAdminGuard>}
                  />
                  <Route
                    path="/admin/programs/applications"
                    element={<SuperAdminGuard><Applications /></SuperAdminGuard>}
                  />
                  {/* High Code Management Routes */}
                  <Route path="/admin/highcode/projects" element={<Projects />} />
                  <Route path="/admin/highcode/games" element={<Games />} />
                  <Route path="/admin/highcode/starter-packs" element={<StarterPacks />} />
                  <Route path="/admin/highcode/categories" element={<Categories />} />
                  <Route path="/admin/highcode/age-groups" element={<AgeGroups />} />
                  <Route path="/admin/highcode/content" element={<Content />} />
                </Route>

                <Route
                  path="/instructor/classroom/:roomId"
                  element={<InstructorClassroom />}
                />

                <Route
                  path="/apply"
                  element={
                    <LandingLayout>
                      <Apply />
                    </LandingLayout>
                  }
                />
              </Routes>
            </Router>
          </AuthProvider>
        </AlertProvider>
      </Provider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
